import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useLocale } from '../../context';
import { api } from '../../api';
import {
  createPlatformCredential,
  dismissPasskeyOffer,
  markLocalPasskeyHint,
  mapWebAuthnError,
} from '../../utils/webauthn';

export default function PasskeyOfferModal({ open, onClose }) {
  const _locale = useLocale();
  const tr = _locale.t;
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const close = (dismiss = false) => {
    if (dismiss) dismissPasskeyOffer();
    onClose?.();
  };

  const register = async () => {
    setError('');
    setLoading(true);
    try {
      const options = await api.webauthnRegisterOptions(token);
      const { challengeId, ...publicKey } = options;
      const attestation = await createPlatformCredential(publicKey);
      if (!attestation) throw new Error(tr('auth.webauthn.registerFailed'));
      await api.webauthnRegisterVerify(token, {
        challengeId,
        response: attestation,
        deviceName: tr('auth.webauthn.thisDevice'),
      });
      markLocalPasskeyHint();
      dismissPasskeyOffer();
      onClose?.();
    } catch (err) {
      const code = mapWebAuthnError(err);
      if (code === 'cancelled') setError('');
      else if (code === 'already_registered') setError(tr('auth.webauthn.alreadyRegistered'));
      else setError(err.message || tr('auth.webauthn.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="auth-modal-overlay" onClick={() => close(true)}>
      <div className="auth-modal auth-passkey-offer" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h2 className="auth-modal__title">{tr('auth.webauthn.offerTitle')}</h2>
        <p className="auth-passkey-offer__text">{tr('auth.webauthn.offerBody')}</p>
        {error ? <div className="auth-error">{error}</div> : null}
        <button type="button" className="auth-submit" onClick={register} disabled={loading}>
          {loading ? '...' : tr('auth.webauthn.offerConfirm')}
        </button>
        <button type="button" className="auth-passkey-offer__skip" onClick={() => close(true)} disabled={loading}>
          {tr('auth.webauthn.offerSkip')}
        </button>
      </div>
    </div>,
    document.body,
  );
}
